import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
  Avatar,
  Button,
  Select,
  MenuItem,
  Chip,
  Skeleton,
  Link,
} from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { SEO } from "../../components/common/SEO";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { CenteredLoading } from "../../components/common/CenteredLoading";
import ToyCard from "../../components/product/ToyCard";
import {
  sellerProductsForProfile,
  SORT_BY,
} from "../../redux/actions/productActions";
import { RootState } from "../../redux/store";
import { Product } from "../../redux/types/productTypes";
import {
  formatDate,
  getInitialWithUpperCase,
  getLoginCookieAndSession,
  removeDuplicates,
  sendConfirmationEmail,
} from "../../utils";
import { TotalResults } from "../../components/product/TotalResults";
import { DrawerModal } from "../../components/common/DrawerModal";
import Login from "../../components/common/Login";
import SnackbarAlert, {
  SnackbarRef,
} from "../../components/common/SnackbarAlert";
import { fetchUserByUniqueId } from "../../redux/actions/userActions";
import useFetchUserDetails from "../../hooks/useFetchCurrentUserDetails";
import { MenuList } from "../../components/common/MenuList";

const PAGE_SIZE = 18;

const SellerProfile: React.FC = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();

  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<SORT_BY>(SORT_BY.LATEST);
  const [isReloadFromFilter, setIsReloadFromFilter] = useState<boolean>(false);
  const [isAccountModalOpen, setAccountModalOpen] = React.useState(false);
  const [isAlertVerifyUserModalOpen, setIsAlertVerifyUserModalOpen] =
    React.useState(false);

  const snackbarRef = useRef<SnackbarRef>(null);

  const dispatch = useDispatch();
  const { loading, productsData, error } = useSelector(
    (state: RootState) => state.products
  );
  const { products, sellerInfo } = productsData;
  const { users } = useFetchUserDetails();

  useEffect(() => {
    if (products && products.length) {
      const uniqueArray = removeDuplicates(
        [...allProducts, ...products],
        "createdTime"
      );
      setAllProducts(uniqueArray);
    }
  }, [products]);

  useEffect(() => {
    setAllProducts([]);
    if (profileId) {
      dispatch(
        sellerProductsForProfile(
          Number(profileId),
          "",
          selectedFilter,
          PAGE_SIZE
        )
      );
    } else {
      navigate("/");
    }
  }, [dispatch, profileId]);

  const filterHandler = (value: any) => {
    setIsReloadFromFilter(true);
    setAllProducts([]);
    setSelectedFilter(value);
    if (profileId) {
      dispatch(
        sellerProductsForProfile(Number(profileId), "", value, PAGE_SIZE)
      );
    }
  };

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

    if (
      profileId &&
      scrollTop + clientHeight >= scrollHeight &&
      products.length > 0 &&
      !loading
    ) {
      dispatch(
        sellerProductsForProfile(
          Number(profileId),
          products[products.length - 1].documentId,
          selectedFilter,
          PAGE_SIZE
        )
      );
    }
  }, [products, dispatch, loading]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const handleMailTo = () => {
    if (users && users.length) {
      if (users[0].isVerified) {
        const email = sellerInfo.email;
        const subject = "Your items on FiddlePiddle";
        const body = "Hello,";

        const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(body)}`;
        window.open(mailtoLink, "_blank");
      } else {
        setIsAlertVerifyUserModalOpen(true);
      }
    } else {
      setAccountModalOpen(true);
    }
  };

  const handleSendConfirmEmail = async () => {
    if (users && users.length) {
      await sendConfirmationEmail(users[0].email, users[0].createdTime);
      snackbarRef.current?.showSnackbar(
        "Confirmation email resent. Please check your inbox.",
        "success"
      );
      setIsAlertVerifyUserModalOpen(false);
    }
  };

  return !isReloadFromFilter && allProducts.length === 0 ? (
    <CenteredLoading />
  ) : (
    <>
      {sellerInfo && (
        <SEO
          title={`${sellerInfo.name}'s Profile • FiddlePiddle • Toys Marketplace`}
          description={
            "Explore a wide range of toys on our marketplace. Buy, sell, and discover unique toys from collectors, sellers, and enthusiasts. Find your next favorite toy today!"
          }
          keywordsContent={`toys marketplace, buy toys, sell toys, collectible toys, vintage toys, kids toys, toy deals, rare toys, toy shop, toy sales, toy collectors`}
        />
      )}
      <div style={{ position: "relative", zIndex: 100 }}>
        <Breadcrumb
          items={[
            { text: "Home", url: "/" },
            { text: "Profile", url: "" },
          ]}
        />
      </div>
      <Container maxWidth="xl" sx={{ paddingTop: 0 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ p: 2 }}
        >
          {sellerInfo && sellerInfo.name ? (
            <>
              <Avatar sx={{ width: 56, height: 56, mb: 1 }}>
                {getInitialWithUpperCase(sellerInfo.name)}
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", mb: 1 }}
                gutterBottom
              >
                {sellerInfo.name}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                textAlign="center"
                mb={1}
              >
                Member since: {formatDate(sellerInfo.createdTime, false)}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                textAlign="center"
                mb={2}
              >
                Location: Los Angeles, CA
              </Typography>
            </>
          ) : (
            <>
              <Skeleton
                variant="circular"
                width={56}
                height={56}
                sx={{ mb: 1 }}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width={120}
                height={32}
                sx={{ mb: 1 }}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width={160}
                height={24}
                sx={{ mb: 2 }}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width={140}
                height={24}
                sx={{ mb: 2 }}
                animation="wave"
              />
            </>
          )}

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mb={2}
          >
            <Chip
              label="Email Verified"
              icon={<CheckCircleIcon />}
              color="secondary"
              size="small"
            />
            <Chip
              label="Phone Verified"
              icon={<CheckCircleIcon />}
              color="secondary"
              size="small"
            />
            <Chip
              label="ID Verified"
              icon={<CheckCircleIcon />}
              color="secondary"
              size="small"
            />
          </Box>

          <Chip
            size="small"
            label="Contact Seller"
            icon={<PersonOutlineOutlinedIcon />}
            onClick={handleMailTo}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            marginTop: "16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
              fontSize: "16px",
            }}
          >
            <span
              style={{
                color: "#666",
                fontWeight: "300",
                fontSize: "14px",
              }}
            >
              <TotalResults quantity={productsData.totalRecords} />
            </span>
            <TuneOutlinedIcon />
            <MenuList
              menuItems={[
                { label: "Sort by:", value: "", isDisabled: true },
                { label: "Latest", value: SORT_BY.LATEST },
                { label: "Popularity", value: SORT_BY.POPULAR },
                { label: "Lowest Price", value: SORT_BY.LOWEST_PRICE },
                { label: "Highest Price", value: SORT_BY.HIGHEST_PRICE },
              ]}
              filterHandler={filterHandler}
              defaultValue={SORT_BY.LATEST}
            />
          </Box>
        </Box>
      </Container>
      <Box bgcolor="#f0f0f0" padding="16px 0 32px" margin="0px">
        <Typography
          variant="h6"
          sx={{
            marginBottom: "16px",
            textAlign: "center",
          }}
        >
          Items from this seller
        </Typography>

        <Grid container spacing={2} style={{ padding: 16 }}>
          {loading && allProducts.length === 0
            ? Array.from({ length: 1 }).map((_, index) => (
                <Grid item key={index} xs={12}>
                  <Box display="flex" justifyContent="center">
                    <CircularProgress />
                  </Box>
                </Grid>
              ))
            : allProducts.map((product, index) => (
                <Grid item key={index} xs={6} sm={4} md={3} lg={2}>
                  <ToyCard product={product} loading={loading} />
                </Grid>
              ))}
          {loading && allProducts.length >= PAGE_SIZE && (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" marginTop={2}>
                <CircularProgress />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
      <SnackbarAlert ref={snackbarRef} />
      <DrawerModal
        isOpen={isAccountModalOpen}
        onClose={() => {
          setAccountModalOpen(false);
        }}
        anchor="bottom"
        onOpen={() => {}}
      >
        <Login
          openFormFrom={"User"}
          alertMessage="You must be signed in to perform that action."
          onLogin={() => {
            setAccountModalOpen(false);
            snackbarRef.current?.showSnackbar(
              "Signed in successfully",
              "success"
            );
          }}
          isOpenFromOpenOtherPage
        />
      </DrawerModal>
      <DrawerModal
        isOpen={isAlertVerifyUserModalOpen}
        onClose={() => {
          setIsAlertVerifyUserModalOpen(false);
        }}
        anchor="bottom"
        onOpen={() => {}}
      >
        <p>
          You must confirm your account to continue. Look for the confirmation
          email in your inbox, or{" "}
          <Link className="clickableLink" onClick={handleSendConfirmEmail}>
            click here
          </Link>{" "}
          to resend it.
        </p>
      </DrawerModal>
    </>
  );
};

export default SellerProfile;
